import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Languages } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = ["Buying a home", "Selling a home", "Refinancing", "General enquiry"];

const inputCls =
  "w-full bg-transparent border border-white/20 px-5 py-4 text-sm text-bone placeholder:text-bone/30 focus:outline-none focus:border-copper focus:ring-1 focus:ring-copper transition-colors duration-300";

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: interests[0], message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPrefill = (e) =>
      setForm((f) => ({
        ...f,
        interest: e.detail.interest || "Buying a home",
        message: e.detail.message ?? e.detail,
      }));
    window.addEventListener("opeam:prefill", onPrefill);
    return () => window.removeEventListener("opeam:prefill", onPrefill);
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, lang });
      toast.success(t.contact.toastOk);
      setForm({ name: "", email: "", phone: "", interest: interests[0], message: "" });
    } catch (err) {
      toast.error(t.contact.toastErr);
    } finally {
      setSending(false);
    }
  };

  const people = [
    { name: "Omar Castro", role: t.contact.omarRole, phone: "602-748-7239", email: "ocastro@pricemortgage.com", testid: "contact-omar" },
    { name: "Paula Castro", role: t.contact.paulaRole, phone: "(602) 373-2882", email: "paulacastroazrealtor@gmail.com", testid: "contact-paula" },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-24 sm:py-32 lg:py-40 grid grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-5"
        >
          <p className="text-xs tracking-[0.35em] uppercase text-copper">{t.contact.eyebrow}</p>
          <h2 className="mt-8 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.98]">
            {t.contact.h2a} <span className="italic text-copper">{t.contact.h2b}</span>
          </h2>
          <p className="mt-10 max-w-sm text-base leading-relaxed text-bone/60">
            {t.contact.body}
          </p>
          <div className="mt-14 space-y-8">
            {people.map((p) => (
              <div key={p.testid} data-testid={p.testid} className="border-l border-copper/50 pl-5">
                <p className="font-display text-xl tracking-tight">{p.name}</p>
                <p className="text-[10px] tracking-[0.3em] uppercase text-copper mt-1">{p.role}</p>
                <div className="mt-3 space-y-1 text-sm text-bone/50">
                  <a href={`tel:${p.phone.replace(/\D/g, "")}`} className="block hover:text-copper transition-colors duration-300 w-fit">
                    {p.phone}
                  </a>
                  <a href={`mailto:${p.email}`} className="block hover:text-copper transition-colors duration-300 w-fit">
                    {p.email}
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p data-testid="contact-service-area" className="mt-12 text-[10px] tracking-[0.3em] uppercase text-bone/30">
            {t.contact.serviceArea} <span className="text-copper">{t.contact.area}</span>
          </p>
          <p data-testid="contact-espanol" className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-copper/80">
            <Languages size={13} />
            {t.contact.espanol}
          </p>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-7 lg:pt-16 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <input
              data-testid="contact-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder={t.contact.name}
              className={inputCls}
            />
            <input
              data-testid="contact-email-input"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder={t.contact.email}
              className={inputCls}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <input
              data-testid="contact-phone-input"
              value={form.phone}
              onChange={set("phone")}
              placeholder={t.contact.phone}
              className={inputCls}
            />
            <select
              data-testid="contact-interest-select"
              value={form.interest}
              onChange={set("interest")}
              className={`${inputCls} appearance-none cursor-pointer`}
            >
              {interests.map((i) => (
                <option key={i} value={i} className="bg-ink text-bone">
                  {t.contact.interests[i]}
                </option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="contact-message-input"
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder={t.contact.message}
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="btn-swipe group flex items-center gap-3 border border-copper text-copper px-10 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-300 disabled:opacity-50"
          >
            {sending ? (
              <>
                {t.contact.sending} <Loader2 size={15} className="animate-spin" />
              </>
            ) : (
              <>
                {t.contact.submit}
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
